import { Country } from '@commenda/prisma';

import { Job, JobStatus } from '../../new-sales-tax/job/types';

// ─── Nexus Types ───

export enum TIndirectTaxExposureNexusStatus {
  NexusTriggered = 'NexusTriggered',
  ApproachingNexus = 'ApproachingNexus',
  NoNexus = 'NoNexus',
  Remittance = 'Remittance',
}

export enum TIndirectTaxExposureEvaluationPeriod {
  PREVIOUS_OR_CURRENT_CALENDAR_YEAR = 'PREVIOUS_OR_CURRENT_CALENDAR_YEAR',
  PREVIOUS_CALENDAR_YEAR = 'PREVIOUS_CALENDAR_YEAR',
  PRECEDING_12_MONTHS = 'PRECEDING_12_MONTHS',
  PRECEDING_FISCAL_YEAR = 'PRECEDING_FISCAL_YEAR',
  PRECEDING_12_MONTHS_ENDING_SEPT_30 = 'PRECEDING_12_MONTHS_ENDING_SEPT_30',
}

export interface TIndirectTaxExposureTaxLiability {
  estimated_tax_local_currency: number;
  currency: string;
  transaction_count: number;
  total_sales_post_breach: number;
}

export type TIndirectTaxExposureNexus = {
  state: string;
  status: TIndirectTaxExposureNexusStatus;
  evaluationPeriod: TIndirectTaxExposureEvaluationPeriod;
  nexusBreached: boolean;
  dateOfBreach: string;
  sales: {
    threshold: number | null;
    current: number;
    percentage: number;
    showPercentage: boolean;
  };
  transactions: {
    threshold: number | null;
    current: number;
    percentage: number;
    showPercentage: boolean;
  };
  taxLiability?: TIndirectTaxExposureTaxLiability;
  jurisdiction?: string | null;
};

export interface TIndirectTaxRecalculateExposureTaxEstimatesResponse {
  data: {
    job_id: string;
    job_type: string;
    status: string;
    progress: number;
    total_invoices: number;
    processing_mode: string;
    estimated_time_ms: number;
  };
  message: string;
}

// ─── Exposure V2 Types (from new-sales-tax/exposure endpoint) ───

export interface TIndirectTaxExposureV2Rule {
  salesThreshold: number | null;
  transactionsThreshold: number | null;
  thresholdType: string;
  evaluationPeriodType: string;
  locationBasedScope: string;
  exclusionTypes: string[];
  establishmentTypes: string[];
  maxTransactionValue?: number;
}

export interface TIndirectTaxExposureV2Calculation {
  includedSales: number;
  includedTransactions: number;
  grossSales: number;
  grossTransactions: number;
  salesExposurePercentage: number;
  transactionsExposurePercentage: number;
  /** Total gross sales into the jurisdiction on/after the breach date (0 when not breached). */
  grossSalesAfterBreach: number;
}

export interface TIndirectTaxExposureV2Jurisdiction {
  jurisdictionId: string;
  jurisdictionType: string;
  jurisdictionName: string;
  exposureType: string;
  exposureRuleId: string;
  subdivision?: string;
  currency: string;
  rule: TIndirectTaxExposureV2Rule;
  calculation: TIndirectTaxExposureV2Calculation;
  isNexusBreached: boolean;
  dateOfBreach?: string;
  recommendedRegistrationContentIds?: string[];
}

export interface TIndirectTaxExposureV2Response {
  corporationId: string;
  jurisdictions: TIndirectTaxExposureV2Jurisdiction[];
}

// ─── Async sync + job polling (Part 1: SQS-backed exposure sync) ───
// Job records use the canonical Job / JobStatus types from
// new-sales-tax/job/types. The frontend polls the generic /new-sales-tax/jobs
// endpoint filtered by entity=EXPOSURE_SYNC — there is no exposure-specific
// job shape or status enum.

/** Result envelope returned by the `generateIndirectTaxExposure` mutation. 202 success and 409 already-running both resolve with `success: true`. */
export interface IIndirectTaxGenerateExposureMutationResult {
  success: boolean;
  message?: string;
  /** Set on success or 409 — the job id the caller can subscribe to via the jobs list endpoint. */
  jobId?: string;
  status?: JobStatus;
  error?: string;
}

/** Inputs accepted by the `useIndirectTaxExposureJobs` polling hook. */
export interface IUseIndirectTaxExposureJobsParams {
  corporationId: number;
  limit?: number;
}

/** Shape returned by the `useIndirectTaxExposureJobs` polling hook. */
export interface IUseIndirectTaxExposureJobsResult {
  jobs: Job[];
  totalJobs: number;
  isLoading: boolean;
  error: unknown;
  /** True when any job in the latest N is PENDING / PROCESSING / QUEUED. Drives loading UI + polling. */
  isRunning: boolean;
  /** completed_at (microseconds since epoch) of the most recent COMPLETED job, or null. */
  latestCompletedAt: number | null;
  /** failure_reasons[0] of the most recent terminal job when it's FAILED, else null. */
  latestFailureMessage: string | null;
  mutate: () => Promise<unknown>;
}

// ─── Map Exposure Types ───

export interface TIndirectTaxPhysicalExposure {
  employee: {
    count: number;
  };
  location: {
    office: {
      count: number;
    };
  };
}

export interface TIndirectTaxEconomicExposure {
  transactions: {
    total: number;
    volume: number;
  };
}

/** Map layer: physical vs economic vs entity (API / map-exposure). */
export enum TIndirectTaxExposureLayerType {
  PhysicalExposure = 'PhysicalExposure',
  EconomicExposure = 'EconomicExposure',
  EntityExposure = 'EntityExposure',
}

export interface TIndirectTaxExposureCorporation {
  id: number;
  corporationDisplayName: string;
  incorporationJurisdictionState: string;
  incorporationJurisdictionStateName: string;
  incorporationCountry: Country;
  physical: TIndirectTaxPhysicalExposure | null;
  economic: TIndirectTaxEconomicExposure | null;
  types: TIndirectTaxExposureLayerType[];
}

export interface TIndirectTaxExposureState {
  code: string;
  corporations: TIndirectTaxExposureCorporation[];
  physical: TIndirectTaxPhysicalExposure | null;
  economic:
    | (TIndirectTaxEconomicExposure & { threshold: number | null })
    | null;
  nexus: Omit<TIndirectTaxExposureNexus, 'state'> | null;
  types: TIndirectTaxExposureLayerType[];
}

export interface TIndirectTaxExposureMap {
  exposures: {
    countries: {
      code: Country;
      states: TIndirectTaxExposureState[];
      corporations: TIndirectTaxExposureCorporation[];
      physical: TIndirectTaxPhysicalExposure | null;
      economic: TIndirectTaxEconomicExposure | null;
      types: TIndirectTaxExposureLayerType[];
    }[];
    physical: TIndirectTaxPhysicalExposure | null;
    economic: TIndirectTaxEconomicExposure | null;
  };
}
